// src/services/offersService.js
import { apiRequest } from "./apiService";
import { buildQueryString } from "./queryString";
import { uploadMultipart } from "./httpUpload";

const DESTINATION_MODE = Object.freeze({
  LOCREACH_PAGE: "LOCREACH_PAGE",
  EXTERNAL_URL: "EXTERNAL_URL",
  SOCIAL_OR_LOCAL_PROFILE: "SOCIAL_OR_LOCAL_PROFILE",
});

const CUSTOMER_ACTION_TYPE = Object.freeze({
  CLAIM_OFFER: "CLAIM_OFFER",
  VISIT_PAGE: "VISIT_PAGE",
  BOOKING_LINK: "BOOKING_LINK",
  CALL_BUSINESS: "CALL_BUSINESS",
  SEND_WHATSAPP: "SEND_WHATSAPP",
  OPEN_INSTAGRAM: "OPEN_INSTAGRAM",
  OPEN_GOOGLE_MAPS: "OPEN_GOOGLE_MAPS",
});

const encId = (id) =>
  encodeURIComponent(String(id == null ? "" : id).trim());

const toStr = (v) => String(v == null ? "" : v).trim();


const IMAGE_UPLOAD_ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const IMAGE_UPLOAD_EXTENSIONS_BY_TYPE = Object.freeze({
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
});

const IMAGE_UPLOAD_MAX_BYTES = 3 * 1024 * 1024;

export const sanitizePromotionImageFileName = (
  fileName = "promotion-image",
  contentType = "image/jpeg"
) => {
  const safeContentType = IMAGE_UPLOAD_ALLOWED_TYPES.has(contentType)
    ? contentType
    : "image/jpeg";

  const extensionFromType =
    IMAGE_UPLOAD_EXTENSIONS_BY_TYPE[safeContentType] || "jpg";

  const rawName = String(fileName || "promotion-image")
    .replace(/\0/g, "")
    .trim();

  const baseName = rawName.split(/[\\/]/).pop() || "promotion-image";
  const nameWithoutExtension = baseName.replace(/\.[^.]*$/, "");

  const safeBase =
    nameWithoutExtension
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "promotion-image";

  return `${safeBase.slice(0, 80)}.${extensionFromType}`;
};

export const validatePromotionImageFile = (file) => {
  if (!file) {
    return { ok: false, message: "Choose a promotion image." };
  }

  if (!IMAGE_UPLOAD_ALLOWED_TYPES.has(file.type)) {
    return {
      ok: false,
      message: "Upload a JPG, PNG or WebP image.",
    };
  }

  if (!Number.isFinite(file.size) || file.size <= 0) {
    return {
      ok: false,
      message: "The selected image is empty or cannot be read.",
    };
  }

  if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
    return {
      ok: false,
      message: "Image must be under 3MB.",
    };
  }

  return {
    ok: true,
    safeFileName: sanitizePromotionImageFileName(file.name, file.type),
    contentType: file.type,
    fileSize: file.size,
  };
};

const toNullableStr = (v) => {
  const s = toStr(v);
  return s || null;
};

const toUndefinedStr = (v) => {
  const s = toStr(v);
  return s || undefined;
};

const toBoolStrict = (v) => {
  if (v === true || v === false) return v;
  if (v == null) return v;

  const s = String(v).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;

  return v;
};

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const copyAlias = (target, camelName, snakeName) => {
  if (target[camelName] == null && target[snakeName] != null) {
    target[camelName] = target[snakeName];
  }

  delete target[snakeName];
};

const normalizeEnum = (value, allowedValues) => {
  const normalized = toStr(value).toUpperCase();
  return allowedValues.has(normalized) ? normalized : undefined;
};

const DESTINATION_MODES = new Set(Object.values(DESTINATION_MODE));
const CUSTOMER_ACTION_TYPES = new Set(Object.values(CUSTOMER_ACTION_TYPE));

const normalizeDestinationMode = (value) =>
  normalizeEnum(value, DESTINATION_MODES);

const normalizeCustomerActionType = (value) =>
  normalizeEnum(value, CUSTOMER_ACTION_TYPES);

const normalizeListOffersParams = (params = {}) => {
  const p = { ...(params || {}) };

  if (p.campaign_id != null && p.campaignId == null) {
    p.campaignId = p.campaign_id;
  }
  delete p.campaign_id;

  const cursor =
    p.cursor ??
    p.next_cursor ??
    p.nextCursor ??
    p.last_key ??
    p.lastKey ??
    null;

  if (cursor != null && toStr(cursor) !== "") {
    p.cursor = cursor;
  } else {
    delete p.cursor;
  }

  delete p.next_cursor;
  delete p.nextCursor;
  delete p.last_key;
  delete p.lastKey;

  if ("active" in p) {
    const active = toBoolStrict(p.active);
    if (active === true || active === false) {
      p.active = active;
    } else if (p.active == null || toStr(p.active) === "") {
      delete p.active;
    }
  }

  if (typeof p.q === "string") {
    p.q = p.q.trim();
    if (!p.q) delete p.q;
  }

  if (p.limit != null) {
    const limit = toNum(p.limit, undefined);
    if (Number.isFinite(limit) && limit > 0) {
      p.limit = limit;
    } else {
      delete p.limit;
    }
  }

  return p;
};

const normalizeOfferPayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  copyAlias(p, "campaignId", "campaign_id");
  copyAlias(p, "discountType", "discount_type");
  copyAlias(p, "discountValue", "discount_value");
  copyAlias(p, "landingUrl", "landing_url");
  copyAlias(p, "startDate", "start_date");
  copyAlias(p, "endDate", "end_date");

  // New hosted/public promotion destination contract.
  copyAlias(p, "destinationMode", "destination_mode");
  copyAlias(p, "customerActionType", "customer_action_type");
  copyAlias(p, "customerActionUrl", "customer_action_url");
  copyAlias(p, "landingPageEnabled", "landing_page_enabled");
  copyAlias(p, "businessOpeningHours", "business_opening_hours");
  copyAlias(p, "averageBookingValue", "average_booking_value");
  copyAlias(p, "revenueEstimateMode", "revenue_estimate_mode");
  copyAlias(p, "bookingNotificationEmail", "booking_notification_email");
  copyAlias(p, "coverImageKey", "cover_image_key");
  copyAlias(p, "coverImageUrl", "cover_image_url");
  copyAlias(p, "coverImageAlt", "cover_image_alt");
  copyAlias(p, "coverImageUpdatedAt", "cover_image_updated_at");

  if ("active" in p) {
    p.active = toBoolStrict(p.active);
  }

  if ("landingPageEnabled" in p) {
    p.landingPageEnabled = toBoolStrict(p.landingPageEnabled);
  }

  const destinationMode =
    "destinationMode" in p
      ? normalizeDestinationMode(p.destinationMode)
      : undefined;

  const customerActionType =
    "customerActionType" in p
      ? normalizeCustomerActionType(p.customerActionType)
      : undefined;

  const isHostedLocReachPage =
    destinationMode === DESTINATION_MODE.LOCREACH_PAGE;

  const customerActionUrl =
    "customerActionUrl" in p
      ? toNullableStr(p.customerActionUrl)
      : undefined;

  const out = {
    campaignId:
      "campaignId" in p
        ? toStr(p.campaignId) === ""
          ? ""
          : toUndefinedStr(p.campaignId)
        : undefined,

    title: "title" in p ? toUndefinedStr(p.title) : undefined,
    code: "code" in p ? toUndefinedStr(p.code) : undefined,

    discountType:
      "discountType" in p ? toUndefinedStr(p.discountType) : undefined,

    discountValue:
      "discountValue" in p && p.discountValue !== ""
        ? Number(p.discountValue)
        : undefined,

    // The backend generates landingUrl/publicPageUrl for LOCREACH_PAGE.
    // Keep landingUrl only for external or social/local destinations.
    landingUrl:
      !isHostedLocReachPage && "landingUrl" in p
        ? toUndefinedStr(p.landingUrl)
        : undefined,

    destinationMode,
    customerActionType,
    customerActionUrl,

    landingPageEnabled:
      destinationMode != null
        ? isHostedLocReachPage
        : "landingPageEnabled" in p
          ? p.landingPageEnabled
          : undefined,

    active: "active" in p ? p.active : undefined,
    currency: "currency" in p ? toUndefinedStr(p.currency) : undefined,

    bookingNotificationEmail:
      "bookingNotificationEmail" in p
        ? toUndefinedStr(p.bookingNotificationEmail)?.toLowerCase()
        : undefined,

    coverImageKey:
      "coverImageKey" in p ? toUndefinedStr(p.coverImageKey) : undefined,
    coverImageUrl:
      "coverImageUrl" in p ? toUndefinedStr(p.coverImageUrl) : undefined,
    coverImageAlt:
      "coverImageAlt" in p ? toUndefinedStr(p.coverImageAlt) : undefined,
    coverImageUpdatedAt:
      "coverImageUpdatedAt" in p
        ? toUndefinedStr(p.coverImageUpdatedAt)
        : undefined,

    businessOpeningHours:
      "businessOpeningHours" in p
        ? toUndefinedStr(p.businessOpeningHours)
        : undefined,

    averageBookingValue:
      "averageBookingValue" in p && p.averageBookingValue !== ""
        ? Number(p.averageBookingValue)
        : undefined,

    revenueEstimateMode:
      "revenueEstimateMode" in p ? toUndefinedStr(p.revenueEstimateMode) : undefined,

    budget:
      "budget" in p && p.budget !== ""
        ? Number(p.budget)
        : undefined,

    startDate: "startDate" in p ? toUndefinedStr(p.startDate) : undefined,
    endDate: "endDate" in p ? toUndefinedStr(p.endDate) : undefined,
  };

  Object.keys(out).forEach((key) => {
    if (out[key] === undefined) {
      delete out[key];
    }
  });

  return out;
};

const normalizeOffer = (raw = {}) => {
  const offerId = raw.offerId || raw.offer_id || raw.id || "";
  const destinationMode =
    raw.destinationMode ||
    raw.destination_mode ||
    (raw.landingPageEnabled || raw.landing_page_enabled
      ? DESTINATION_MODE.LOCREACH_PAGE
      : DESTINATION_MODE.EXTERNAL_URL);

  const landingPageEnabledRaw =
    raw.landingPageEnabled ?? raw.landing_page_enabled;

  return {
    ...raw,
    offerId,
    id: offerId,

    campaignId: raw.campaignId || raw.campaign_id || null,
    title: raw.title || "",
    code: raw.code || "",

    discountType: raw.discountType || raw.discount_type || "",
    discountValue: toNum(
      raw.discountValue ?? raw.discount_value,
      0
    ),

    landingUrl: raw.landingUrl || raw.landing_url || "",

    destinationMode,
    customerActionType:
      raw.customerActionType || raw.customer_action_type || "",
    customerActionUrl:
      raw.customerActionUrl ?? raw.customer_action_url ?? null,

    landingPageEnabled:
      landingPageEnabledRaw == null
        ? destinationMode === DESTINATION_MODE.LOCREACH_PAGE
        : Boolean(landingPageEnabledRaw),

    pageSlug: raw.pageSlug || raw.page_slug || "",
    publicPageUrl: raw.publicPageUrl || raw.public_page_url || "",

    status: raw.status || raw.pageStatus || raw.page_status || "",
    expired: Boolean(raw.expired),
    active: Boolean(raw.active),
    currency: raw.currency || "GBP",

    bookingNotificationEmail:
      raw.bookingNotificationEmail ??
      raw.booking_notification_email ??
      raw.notificationEmail ??
      raw.notification_email ??
      null,
    businessOpeningHours:
      raw.businessOpeningHours ?? raw.business_opening_hours ?? "",
    averageBookingValue: toNum(
      raw.averageBookingValue ?? raw.average_booking_value,
      0
    ),
    revenueEstimateMode:
      raw.revenueEstimateMode ??
      raw.revenue_estimate_mode ??
      (toNum(raw.averageBookingValue ?? raw.average_booking_value, 0) > 0
        ? "AVERAGE_BOOKING_VALUE"
        : ""),

    coverImageKey: raw.coverImageKey ?? raw.cover_image_key ?? null,
    coverImageUrl: raw.coverImageUrl ?? raw.cover_image_url ?? "",
    coverImageAlt: raw.coverImageAlt ?? raw.cover_image_alt ?? "",
    coverImageUpdatedAt:
      raw.coverImageUpdatedAt ?? raw.cover_image_updated_at ?? null,

    createdAt: raw.createdAt || raw.created_at || "",
    updatedAt: raw.updatedAt || raw.updated_at || null,
  };
};

const normalizeOfferListResponse = (raw = {}) => {
  const items = Array.isArray(raw?.items)
    ? raw.items.map(normalizeOffer)
    : [];

  const lastKey = raw?.lastKey || raw?.last_key || null;
  const nextCursor = raw?.nextCursor || raw?.next_cursor || null;

  return {
    ...raw,
    items,
    count: toNum(raw?.count, items.length),
    last_key: lastKey,
    lastKey,
    next_cursor: nextCursor,
    nextCursor,
  };
};

const normalizeTopInvite = (raw = null) => {
  if (!raw || typeof raw !== "object") return null;

  return {
    inviteId: raw.inviteId || raw.id || "",
    clicks: toNum(raw.clicks, 0),
    redemptions: toNum(raw.redemptions, 0),
    revenue: toNum(raw.revenue, 0),
  };
};

const normalizeOfferOverviewQuery = (params = {}) => {
  const p = { ...(params || {}) };

  if (typeof p.from === "string") p.from = p.from.trim();
  if (typeof p.to === "string") p.to = p.to.trim();

  if (!p.from) delete p.from;
  if (!p.to) delete p.to;

  return p;
};

const normalizeOfferOverview = (raw = {}) => {
  return {
    ...raw,
    offerId: raw.offerId || raw.offer_id || "",
    from: raw.from || null,
    to: raw.to || null,
    clicks: toNum(raw.clicks, 0),
    redemptions: toNum(raw.redemptions, 0),
    revenue: toNum(raw.revenue, 0),
    conversionRate: toNum(
      raw.conversionRate ?? raw.conversion_rate,
      0
    ),
    topInvite: normalizeTopInvite(raw.topInvite || raw.top_invite),
  };
};

export const createOffer = async (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to create an offer.");
  }

  const normalizedPayload = normalizeOfferPayload(payload);
  const res = await apiRequest("/offers", "POST", normalizedPayload);

  return normalizeOffer(res || {});
};

export const listOffers = async (params = {}) => {
  const qs = buildQueryString(normalizeListOffersParams(params));
  const res = await apiRequest(`/offers${qs}`, "GET");

  return normalizeOfferListResponse(res || {});
};

export const getOffer = async (offerId) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  const res = await apiRequest(`/offers/${encId(offerId)}`, "GET");
  return normalizeOffer(res || {});
};

export const patchOffer = async (offerId, payload) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to patch offer.");
  }

  const normalizedPayload = normalizeOfferPayload(payload);

  const res = await apiRequest(
    `/offers/${encId(offerId)}`,
    "PATCH",
    normalizedPayload
  );

  // The backend may return either a full offer record or a small mutation
  // result such as { ok, offerId, updatedAt }. Only normalize when the
  // response contains enough fields to be a complete offer; otherwise mark it
  // as mutation-only so reducers do not replace a rich offer with partial data.
  if (res && typeof res === "object") {
    const looksLikeFullOffer =
      "title" in res ||
      "code" in res ||
      "active" in res ||
      "discountType" in res ||
      "discount_type" in res;

    if (looksLikeFullOffer) {
      return normalizeOffer(res);
    }

    return { ...res, __mutationOnly: true };
  }

  return { ok: true, __mutationOnly: true };
};

/**
 * Keep only if the backend supports DELETE /offers/{offerId}.
 */
export const deleteOffer = async (offerId) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  return apiRequest(`/offers/${encId(offerId)}`, "DELETE");
};

/**
 * GET /offers/{offerId}/overview?from=&to=
 */
export const getOfferOverview = async (offerId, params = {}) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  const qs = buildQueryString(normalizeOfferOverviewQuery(params));
  const res = await apiRequest(
    `/offers/${encId(offerId)}/overview${qs}`,
    "GET"
  );

  return normalizeOfferOverview(res || {});
};


const normalizeCoverImageUploadResponse = (uploadData = {}) => {
  const fields =
    uploadData.fields ||
    uploadData.formFields ||
    uploadData.form_fields ||
    {};

  const method =
    uploadData.method ||
    uploadData.uploadMethod ||
    uploadData.upload_method ||
    "POST";

  const imageKey =
    uploadData.imageKey ||
    uploadData.image_key ||
    uploadData.patchPayload?.coverImageKey ||
    uploadData.patch_payload?.cover_image_key ||
    fields.key ||
    "";

  const publicUrl =
    uploadData.publicUrl ||
    uploadData.public_url ||
    uploadData.coverImageUrl ||
    uploadData.cover_image_url ||
    uploadData.patchPayload?.coverImageUrl ||
    uploadData.patch_payload?.cover_image_url ||
    "";

  return {
    ...uploadData,
    fields,
    method,
    imageKey,
    publicUrl,
  };
};

/**
 * POST /offers/{offerId}/cover-image/upload-url
 * Returns an S3 presigned POST form. This does not upload bytes to Lambda.
 */
export const createOfferCoverImageUploadUrl = async (offerId, fileOrPayload) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  const looksLikeFile =
    typeof File !== "undefined" && fileOrPayload instanceof File;

  const contentType =
    fileOrPayload?.contentType || fileOrPayload?.type || "image/jpeg";

  const fileSize = Number(fileOrPayload?.fileSize ?? fileOrPayload?.size ?? 0);

  const rawFileName =
    fileOrPayload?.fileName || fileOrPayload?.name || "promotion-image";

  const safeFileName = sanitizePromotionImageFileName(rawFileName, contentType);

  if (looksLikeFile) {
    const validation = validatePromotionImageFile(fileOrPayload);
    if (!validation.ok) {
      throw new Error(validation.message);
    }
  } else {
    if (!IMAGE_UPLOAD_ALLOWED_TYPES.has(contentType)) {
      throw new Error("Upload a JPG, PNG or WebP image.");
    }

    if (!Number.isFinite(fileSize) || fileSize <= 0) {
      throw new Error("Image file size is required.");
    }

    if (fileSize > IMAGE_UPLOAD_MAX_BYTES) {
      throw new Error("Image must be under 3MB.");
    }
  }

  const res = await apiRequest(
    `/offers/${encId(offerId)}/cover-image/upload-url`,
    "POST",
    {
      // Backend requires a simple image filename. Do not send raw Safari/macOS
      // names such as "Screenshot 2026-06-27 at 12.30.10.png".
      fileName: safeFileName,
      contentType,
      fileSize,
    }
  );

  return normalizeCoverImageUploadResponse({
    ...(res || {}),
    safeFileName,
  });
};

/**
 * Upload the selected file directly to S3 using the presigned POST form.
 * Important: do not set Content-Type manually; the browser must set the
 * multipart boundary. The file field must be appended last.
 */
export const uploadOfferCoverImageToS3 = async (uploadData, file) => {
  if (!uploadData || typeof uploadData !== "object") {
    throw new Error("Upload data is required.");
  }

  const validation = validatePromotionImageFile(file);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const normalizedUpload = normalizeCoverImageUploadResponse(uploadData);
  const uploadUrl = normalizedUpload.uploadUrl || normalizedUpload.url;
  const fields = normalizedUpload.fields || {};

  if (!uploadUrl) {
    throw new Error("Upload URL missing from signed upload response.");
  }

  if (!fields || Object.keys(fields).length === 0) {
    throw new Error("Signed upload fields missing from upload response.");
  }

  const form = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value != null) {
      form.append(key, String(value));
    }
  });

  // Required by S3 presigned POST. Keep this as the final form field and do
  // not set the Content-Type header manually.
  form.append("file", file);

  const response = await fetch(uploadUrl, {
    method: normalizedUpload.method || "POST",
    body: form,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `S3 image upload failed (${response.status}). ${text || "Check media bucket CORS/policy."}`
    );
  }

  return {
    coverImageKey: normalizedUpload.imageKey || fields.key || "",
    coverImageUrl: normalizedUpload.publicUrl || "",
    safeFileName: normalizedUpload.safeFileName || validation.safeFileName,
  };
};

/**
 * CSV import (FormData) — cannot use apiRequest because it forces JSON.
 */
export const importRedemptionsCsv = async (offerId, file) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  if (!file) {
    throw new Error("CSV file is required.");
  }

  const form = new FormData();
  form.append("file", file);

  return uploadMultipart(
    `/offers/${encId(offerId)}/redemptions/import`,
    form
  );
};
